import { useMemo } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Grid, Box } from "@mui/material";
import SubForm from "./subForm";
import { useVisitorCountry } from "../../hooks/useVisitorCountry";
import { officeCountry } from "../../utils/detectCountry";

function Form() {
  const ContainerBox = styled(Box)(({ theme }) => ({
    display: "flex",
    margin: "auto",
    padding: "64px 0",
    justifyContent: "center",
    backgroundColor: "#fff",
  }));
  return (
    <ContainerBox sx={{padding: {
      xs: 3,
      md: 'unset'
    }}}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <SubForm />
      </Box>
    </ContainerBox>
  );
}

function Top() {
  const ContainerTop = styled("div")(({ theme }) => ({
    backgroundColor: "rgba(211, 242, 226, 1)",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    [theme.breakpoints.up("xl")]: {
      height: 194,
    },
    [theme.breakpoints.down("xl")]: {
      height: 194,
    },
    [theme.breakpoints.down("lg")]: {
      height: 194,
    },
    [theme.breakpoints.down("md")]: {
      height: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      height: "auto",
    },
    [theme.breakpoints.down("xs")]: {
      height: "auto",
    },
  }));

  const PTitle = styled("div")(() => ({
    fontSize: 30,
    fontWeight: 700,
    color: "rgba(49, 55, 61, 1)",
    paddingTop: "53px",
    paddingBottom: "24px",
  }));

  const PInfo = styled("div")(({ theme }) => ({
    fontSize: 20,
    fontWeight: 400,
    color: "rgba(49, 55, 61, 1)",
    paddingBottom: "53px",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.up("xl")]: {
      width: 800,
    },
    [theme.breakpoints.down("xl")]: {
      width: 800,
    },
    [theme.breakpoints.down("lg")]: {
      width: 800,
    },
    [theme.breakpoints.down("md")]: {
      width: "auto",
      padding: "0 8px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      padding: "0 8px 8px 8px",
    },
    [theme.breakpoints.down("xs")]: {
      width: "auto",
      padding: "0 8px 8px 8px",
    },
  }));

  let { t } = useTranslation();
  return (
    <ContainerTop>
      <PTitle>{t("contact.title")}</PTitle>
      <PInfo>{t("contact.info")}</PInfo>
    </ContainerTop>
  );
}

const ContainerTop = styled("div")(() => ({
  height: 164,
  backgroundColor: "rgba(244, 244, 245, 1)",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
}));

const PTitle = styled("div")(() => ({
  fontSize: 30,
  fontWeight: 700,
  color: "rgba(49, 55, 61, 1)",
  paddingTop: "24px",
  paddingBottom: "24px",
}));

const PInfo = styled("div")(() => ({
  fontSize: 20,
  fontWeight: 400,
  color: "rgba(49, 55, 61, 1)",
  paddingBottom: "24px",
  textAlign: "center",
}));

const MainTitle = styled("div")(() => ({
  fontSize: 20,
  fontWeight: 700,
  color: "rgba(49, 55, 61, 1)",
  paddingTop: "30px",
  paddingBottom: "16px",
}));

const ContactLink = styled("a")(() => ({
  color: "rgba(49, 55, 61, 1)",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));

function OfficeRow({ city, addr, phone, email, local }) {
  return (
    <>
      <Grid
        container
        sx={{
          display: { xs: "none", md: "flex" },
          bgcolor: local ? "rgba(211, 242, 226, 0.55)" : "transparent",
        }}
      >
        <Grid item xs={2}></Grid>
        <Grid item xs={8} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <MainTitle>{city}</MainTitle>
            <Grid
              container
              sx={{
                pb: 3,
                borderBottom: "1px solid rgba(204, 204, 204, 1)",
              }}
              spacing={11.875}
            >
              <Grid item xs={4}>
                {addr}
              </Grid>
              <Grid item xs={4}>
                {phone ? (
                  <ContactLink href={`tel:${phone.replace(/\s/g, "")}`}>
                    {phone}
                  </ContactLink>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                {email ? (
                  <ContactLink href={`mailto:${email}`}>{email}</ContactLink>
                ) : null}
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={2}></Grid>
      </Grid>
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          padding: 3,
          gap: 1.5,
          bgcolor: local ? "rgba(211, 242, 226, 0.55)" : "transparent",
        }}
      >
        <MainTitle sx={{ padding: 0, paddingBottom: 2 }}>{city}</MainTitle>
        <Box>{addr}</Box>
        {phone ? (
          <Box>
            <ContactLink href={`tel:${phone.replace(/\s/g, "")}`}>{phone}</ContactLink>
          </Box>
        ) : null}
        {email ? (
          <Box>
            <ContactLink href={`mailto:${email}`}>{email}</ContactLink>
          </Box>
        ) : null}
      </Box>
    </>
  );
}

function Office() {
  const { t } = useTranslation();
  const { country, ready } = useVisitorCountry();
  const offices = useMemo(
    () => [
      {
        country: "CD",
        city: t("contactUs.title1"),
        addr: t("contactUs.subInfo101"),
        phone: t("contactUs.subInfo102"),
        email: t("contactUs.subInfo104"),
      },
      {
        country: "NG",
        city: t("contactUs.title2"),
        addr: t("contactUs.subInfo201"),
        phone: t("contactUs.subInfo202"),
      },
    ],
    [t]
  );

  if (!ready) {
    return null;
  }

  const office = offices.find((item) => item.country === officeCountry(country));
  if (!office || !office.city || !office.addr) {
    return null;
  }

  return <OfficeRow key={office.country} {...office} local />;
}

function Web() {
  const { t } = useTranslation();
  return (
    <>
      <Top />
      <ContainerTop>
        <PTitle>{t("contactUs.text")}</PTitle>
        <PInfo>{t("contactUs.info")}</PInfo>
      </ContainerTop>
      <Office />
      <Form />
    </>
  );
}
export default Web;
